import { useTranslation } from 'react-i18next'
import localizeStripePrice, {
  getStripePriceAmountAndCurrency,
} from '../helpers/localizeStripePrice'
import { Plan, StripePrice } from '../types'
import Dialog from './Dialog'
import SubscriptionForm from './SubscriptionForm'

interface Props {
  currency: string
  isSubmitting: boolean
  onClose: () => void
  open: boolean
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripePriceFull: StripePrice | undefined
  stripePriceIntro?: StripePrice | undefined
}

export default function LimitedTimeOfferDialog({
  currency,
  isSubmitting,
  onClose,
  open,
  plan,
  setIsSubmitting,
  stripePriceFull,
  stripePriceIntro,
}: Props) {
  const { t } = useTranslation()

  if (!stripePriceFull) return null

  const stripePrice = stripePriceIntro ?? stripePriceFull

  const { stripePriceCurrency } = getStripePriceAmountAndCurrency({
    stripePrice,
    userCurrency: currency,
  })

  const isOneTime = plan === 'one-time'
  const { recurring } = stripePriceFull

  const planLabel = recurring
    ? `${recurring.interval_count}-${t(
        `pages.checkout.plans.${recurring.interval}`,
        {
          count: recurring.interval_count,
        },
      )}`
    : t('pages.checkout.plans.oneTimePurchase')

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold">
          {t('pages.checkout.plans.dialog.limitedTimeOffer')}
        </h2>

        <p className="text-xl font-bold tracking-wide uppercase">{planLabel}</p>

        <div className="space-y-2">
          {stripePriceIntro && (
            <p className="text-3xl line-through">
              {localizeStripePrice({
                stripePrice: stripePriceFull,
                userCurrency: currency,
              })}
            </p>
          )}

          <p className="text-5xl font-bold">
            {localizeStripePrice({
              stripePrice,
              userCurrency: currency,
            })}
          </p>
        </div>

        <SubscriptionForm
          buttonClassName="custom-button-primary uppercase"
          buttonTitle={t('pages.checkout.plans.continue')}
          currency={stripePriceCurrency}
          stripePriceFull={stripePriceFull}
          isSubmitting={isSubmitting}
          plan={plan}
          priceId={stripePrice.id}
          setIsSubmitting={setIsSubmitting}
        />

        <div className="space-y-2">
          <p className="font-bold">
            {t('pages.checkout.plans.moneyBackGuarantee')}
          </p>

          {!isOneTime && (
            <p className="text-sm">{t('pages.checkout.plans.cancelAnytime')}</p>
          )}
        </div>
      </div>
    </Dialog>
  )
}
